/* eslint-disable react-hooks/exhaustive-deps */
import { cn } from '@plato/ui/util/classes';
import { isEqual } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductVariantDTO } from '../../../server/dto/product-variant-dto.ts';
import type { GetCatalogProducts } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { AddProductFromCatalog } from './add-product-from-catalog.tsx';

type Props = {
    baseUrl: string;
    lastUrl: string;
    setLastUrl: (url: string) => void;
    onAddLineItem: (product: ProductVariantDTO, quantity: number) => void;
    className?: string;
};

export function Catalog({ baseUrl, lastUrl, setLastUrl, className, onAddLineItem }: Props) {
    const { t } = useTranslation('catalog');
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [currentUrl, setCurrentUrl] = useState('');
    const [newPageCount, setNewPageCount] = useState(0);
    const getProductMutation = api.platoUser.query.catalog.getProductsFromUrl.get.useMutation();
    const [currentPrimaryProduct, setSelectedPrimaryProduct] = useState<GetCatalogProducts | null>(null);

    useEffect(() => {
        if (currentUrl) {
            getProductMutation.mutate(
                { url: currentUrl },
                {
                    onSuccess: (data) => {
                        setSelectedPrimaryProduct(data);
                    },
                    onError: () => {
                        setSelectedPrimaryProduct(null);
                    },
                },
            );
        }
    }, [currentUrl]);

    const onAddProduct = (quantity: number) => {
        if (!currentPrimaryProduct?.mainProduct) {
            return;
        }
        onAddLineItem(currentPrimaryProduct.mainProduct, quantity);
    };

    const isLoadingMutation = getProductMutation.isPending;

    useEffect(() => {
        // Listen for messages from the iframe
        const handleMessage = (event: MessageEvent) => {
            console.log('MESSAGE EVENT', event);
            const cleanBaseUrl = new URL(baseUrl);

            if (event.origin.includes(cleanBaseUrl.origin)) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- legacy code
                if (event.data.type === 'PAGE_UPDATE') {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- legacy code
                    const url = event.data.url;
                    if (!isEqual(currentUrl, url) && typeof url === 'string') {
                        setNewPageCount((prev) => prev + 1);
                        setCurrentUrl(url);
                        setLastUrl(url);
                    } else {
                        console.log('NOT SETTING');
                    }
                }
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    const handleBack = () => {
        const message = { type: 'NAVIGATE_BACK' };
        iframeRef.current?.contentWindow?.postMessage(message, baseUrl);
    };

    const handleForward = () => {
        const message = { type: 'NAVIGATE_FORWARD' };
        iframeRef.current?.contentWindow?.postMessage(message, baseUrl);
    };

    return (
        <div className={cn('flex h-full w-full flex-col', className)}>
            <AddProductFromCatalog
                pageCount={newPageCount}
                isLoading={isLoadingMutation}
                product={currentPrimaryProduct?.mainProduct}
                onAddProduct={onAddProduct}
                onNavigateBack={handleBack}
                onNavigateForward={handleForward}
            >
                <div className="min-h-0 w-full flex-1 bg-neutral-100 p-2">
                    <iframe
                        title={t('iframe_title')}
                        key={lastUrl}
                        ref={iframeRef}
                        src={lastUrl}
                        // transform-gpu promotes the iframe to its own compositing layer so iOS
                        // Safari keeps it touch-scrollable under the dialog's rounded+overflow-hidden card.
                        className="bg-white h-full w-full rounded-lg border border-neutral-200 transform-gpu"
                    ></iframe>
                </div>
            </AddProductFromCatalog>
        </div>
    );
}
